/* Repair Parts Finder — mirrors the flow of the internal estimate builder:
   one stage at a time (brand grid → size buttons → model cards → parts),
   with a sticky trail of step pills plus category + search that work at any
   point. Data comes from the parts-finder API (same normalization the
   internal builder uses). */

if (!customElements.get('repair-parts-finder')) {
  class RepairPartsFinder extends HTMLElement {
    constructor() {
      super();
      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.quickAddEnabled = this.dataset.quickAddEnabled === 'true';
      this.moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD'
      });

      this.facets = null;
      // Catalog scope mirrors the internal builder's tabs: 'repair-parts',
      // 'complete-assemblies', 'enclosures-and-cages', or 'shutoff-valves'.
      // Switching it reloads the facets and products for that catalog, so the
      // catalogs never bleed together. A section can lock a finder to one
      // catalog via data-default-scope (e.g. a standalone Complete Assemblies
      // finder with no toggle); it defaults to Repair Parts.
      const KNOWN_SCOPES = ['repair-parts', 'complete-assemblies', 'enclosures-and-cages', 'shutoff-valves'];
      const requestedScope = this.dataset.defaultScope || 'repair-parts';
      this.scope = KNOWN_SCOPES.includes(requestedScope) ? requestedScope : 'repair-parts';
      this.selectedBrand = '';
      this.selectedSize = '';
      this.selectedModel = '';
      this.selectedCategory = '';
      this.searchTerm = '';
      this.page = 1;
      this.total = 0;
      this.hasMore = false;
      this.fetchSeq = 0;
      this.abortController = null;
      this.searchDebounce = null;
      this.eventsBound = false;

      this.toolbar = this.querySelector('[data-finder-toolbar]');
      this.stagePanel = this.querySelector('[data-stage-panel]');
      this.resultsSection = this.querySelector('[data-results]');
      this.resultsTitle = this.querySelector('[data-results-title]');
      this.resultsMeta = this.querySelector('[data-results-meta]');
      this.emptyMessage = this.querySelector('[data-empty-message]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.resetButton = this.querySelector('[data-reset]');
      this.resultsGrid = this.querySelector('[data-results-grid]');
      this.searchInput = this.querySelector('[data-search-input]');
      this.categorySelect = this.querySelector('[data-category-select]');
      this.scopeToggle = this.querySelector('[data-scope-toggle]');
      this.loadingMessage = this.querySelector('[data-loading-message]');
      this.loadMoreButton = this.querySelector('[data-load-more]');
      this.pills = {
        brand: this.querySelector('[data-pill="brand"]'),
        size: this.querySelector('[data-pill="size"]'),
        model: this.querySelector('[data-pill="model"]'),
        parts: this.querySelector('[data-pill="parts"]'),
      };
      this.pillValues = {
        brand: this.querySelector('[data-pill-value="brand"]'),
        size: this.querySelector('[data-pill-value="size"]'),
        model: this.querySelector('[data-pill-value="model"]'),
        parts: this.querySelector('[data-pill-value="parts"]'),
      };
    }

    connectedCallback() {
      this.loadFacets();
    }

    /* ---- Derived state ---- */

    get stage() {
      if (!this.selectedBrand) return 'brand';
      if (!this.selectedSize) return 'size';
      if (!this.selectedModel) return 'model';
      return 'parts';
    }

    get hasCompleteSelection() {
      return Boolean(this.selectedBrand && this.selectedSize && this.selectedModel);
    }

    /** Search or category show results without finishing the funnel. */
    get hasActiveQuery() {
      return this.hasCompleteSelection || Boolean(this.selectedCategory) || Boolean(this.searchTerm);
    }

    /* ---- Boot ---- */

    /** localStorage key for a scope's cached facets. */
    facetsCacheKey(scope) {
      return `bfd_finder_facets_v1:${scope}`;
    }

    readFacetsCache(scope) {
      try {
        const raw = localStorage.getItem(this.facetsCacheKey(scope));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && parsed.filters ? parsed.filters : null;
      } catch {
        return null;
      }
    }

    writeFacetsCache(scope, filters) {
      try {
        localStorage.setItem(this.facetsCacheKey(scope), JSON.stringify({ filters }));
      } catch {
        /* quota/disabled — caching is best-effort */
      }
    }

    /** Reveal the toolbar + funnel from the current this.facets. */
    showFacets() {
      if (this.loadingMessage) this.loadingMessage.hidden = true;
      if (this.errorMessage) this.errorMessage.hidden = true;
      if (this.toolbar) this.toolbar.hidden = false;
      this.renderCategories();
      this.update();
    }

    async loadFacets() {
      // Bind the scope toggle before the first (async) facets request so it
      // stays responsive even while facets are loading.
      if (!this.eventsBound) {
        this.bindEvents();
        this.eventsBound = true;
      }
      this.renderScopeToggle();

      // Stale-while-revalidate: if we have cached facets for this scope, render
      // the finder INSTANTLY from them (no spinner, no network wait), then
      // refresh quietly in the background. Facets change rarely, so the cached
      // copy is almost always identical to fresh.
      const cached = this.readFacetsCache(this.scope);
      if (cached) {
        this.facets = cached;
        this.showFacets();
      }

      try {
        const query = this.scope !== 'repair-parts' ? `?scope=${encodeURIComponent(this.scope)}` : '';
        const response = await fetch(`${this.apiBase}/api/storefront/parts-finder${query}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Facets request failed (${response.status})`);
        const payload = await response.json();
        if (!payload.success || !payload.filters) throw new Error('Facets payload malformed');
        this.writeFacetsCache(this.scope, payload.filters);
        this.facets = payload.filters;
      } catch (error) {
        console.warn('Repair parts finder could not load facets.', error);
        // Only surface the error if we had no cache to fall back on.
        if (!cached) {
          if (this.loadingMessage) this.loadingMessage.hidden = true;
          if (this.errorMessage) this.errorMessage.hidden = false;
        }
        return;
      }

      // Re-render from fresh facets — but only disrupt the view if the shopper
      // hasn't started a selection yet (otherwise just keep the fresh data for
      // their next step, without yanking the current stage out from under them).
      if (!cached || (this.stage === 'brand' && !this.searchTerm && !this.selectedCategory)) {
        this.showFacets();
      }
    }

    /** Switch catalog scope: reset every selection and reload that catalog's
        facets. The toggle is the storefront equivalent of the builder tabs. */
    setScope(scope) {
      const known = ['repair-parts', 'complete-assemblies', 'enclosures-and-cages', 'shutoff-valves'];
      if (!known.includes(scope)) return;
      if (scope === this.scope) return;
      this.scope = scope;
      this.selectedBrand = '';
      this.selectedSize = '';
      this.selectedModel = '';
      this.selectedCategory = '';
      this.searchTerm = '';
      if (this.searchInput) this.searchInput.value = '';
      if (this.categorySelect) this.categorySelect.value = '';
      this.facets = null;
      this.renderScopeToggle();
      this.loadFacets();
    }

    renderScopeToggle() {
      this.updateStepLabel();
      if (!this.scopeToggle) return;
      this.scopeToggle.querySelectorAll('[data-scope]').forEach((button) => {
        const isActive = button.dataset.scope === this.scope;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    /** Keep the step-4 funnel pill ("4. Repair Parts" / "4. Assemblies" /
        "4. Enclosures") in sync with the active scope, preserving the inner
        value span the pill uses to show the selected count. */
    updateStepLabel() {
      const pill = this.pills.parts;
      if (!pill) return;
      const label =
        this.scope === 'complete-assemblies' ? 'Assemblies'
        : this.scope === 'enclosures-and-cages' ? 'Enclosures'
        : this.scope === 'shutoff-valves' ? 'Valves'
        : 'Repair Parts';
      // The pill's first text node is the "4. X" label; the rest (the
      // data-pill-value span) is left untouched.
      for (const node of pill.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) {
          node.nodeValue = `4. ${label}`;
          return;
        }
      }
    }

    bindEvents() {
      this.stagePanel?.addEventListener('click', (event) => {
        const brandBtn = event.target.closest('[data-pick-brand]');
        if (brandBtn) {
          this.selectedBrand = brandBtn.dataset.pickBrand;
          this.selectedSize = '';
          this.selectedModel = '';
          this.update();
          return;
        }
        const sizeBtn = event.target.closest('[data-pick-size]');
        if (sizeBtn) {
          this.selectedSize = sizeBtn.dataset.pickSize;
          this.selectedModel = '';
          this.update();
          return;
        }
        const modelBtn = event.target.closest('[data-pick-model]');
        if (modelBtn) {
          this.selectedModel = modelBtn.dataset.pickModel;
          this.update();
          this.resultsSection?.scrollIntoView({ block: 'start', behavior: 'smooth' });
          return;
        }
        const back = event.target.closest('[data-stage-back]');
        if (back) {
          if (back.dataset.stageBack === 'brand') {
            this.selectedBrand = '';
            this.selectedSize = '';
            this.selectedModel = '';
          } else if (back.dataset.stageBack === 'size') {
            this.selectedSize = '';
            this.selectedModel = '';
          }
          this.update();
        }
      });

      this.pills.brand?.addEventListener('click', () => {
        this.selectedBrand = '';
        this.selectedSize = '';
        this.selectedModel = '';
        this.update();
      });
      this.pills.size?.addEventListener('click', () => {
        if (!this.selectedBrand) return;
        this.selectedSize = '';
        this.selectedModel = '';
        this.update();
      });
      this.pills.model?.addEventListener('click', () => {
        if (!this.selectedBrand || !this.selectedSize) return;
        this.selectedModel = '';
        this.update();
      });

      this.searchInput?.addEventListener('input', (event) => {
        const value = event.target.value.trim();
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
          // Below two characters the API ignores the term anyway — treat it
          // as "no search" so clearing the input restores the funnel view.
          this.searchTerm = value.length >= 2 ? value : '';
          this.update();
        }, 300);
      });

      this.categorySelect?.addEventListener('change', (event) => {
        this.selectedCategory = event.target.value;
        this.update();
      });

      this.scopeToggle?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-scope]');
        if (button) this.setScope(button.dataset.scope);
      });

      this.loadMoreButton?.addEventListener('click', () => {
        if (this.hasMore) this.fetchProducts(this.page + 1);
      });

      this.resetButton?.addEventListener('click', () => {
        this.selectedBrand = '';
        this.selectedSize = '';
        this.selectedModel = '';
        this.selectedCategory = '';
        this.searchTerm = '';
        if (this.searchInput) this.searchInput.value = '';
        if (this.categorySelect) this.categorySelect.value = '';
        this.update();
      });

      // 2.5"+ devices can't self-checkout — their cards render an "Add to
      // quote" button instead. Hand the item to the global <quote-request>
      // drawer via a custom event (decoupled: the finder doesn't own the
      // drawer or the API call).
      this.resultsGrid?.addEventListener('click', (event) => {
        const quoteBtn = event.target.closest('.js-add-to-quote');
        if (!quoteBtn) return;
        event.preventDefault();
        document.dispatchEvent(
          new CustomEvent('quote:add', {
            detail: {
              variantId: quoteBtn.dataset.variantId,
              title: quoteBtn.dataset.title,
              sku: quoteBtn.dataset.sku || null,
              image: quoteBtn.dataset.image || null,
              handle: quoteBtn.dataset.handle || null,
              quantity: 1,
            },
          })
        );
      });
    }

    /* ---- Master update: pills, stage panel, results ---- */

    update() {
      this.renderPills();
      this.renderStage();

      this.page = 1;
      this.total = 0;
      this.hasMore = false;

      if (this.hasActiveQuery) {
        if (this.resultsSection) this.resultsSection.hidden = false;
        this.fetchProducts(1);
      } else {
        this.abortController?.abort();
        this.fetchSeq += 1; // invalidate any in-flight response
        if (this.resultsSection) this.resultsSection.hidden = true;
        this.resultsGrid.innerHTML = '';
        if (this.emptyMessage) this.emptyMessage.hidden = true;
        if (this.loadMoreButton) this.loadMoreButton.hidden = true;
      }

      if (this.resetButton) {
        this.resetButton.hidden = !this.selectedBrand && !this.searchTerm && !this.selectedCategory;
      }
    }

    renderPills() {
      const stage = this.stage;
      const values = {
        brand: this.selectedBrand,
        size: this.selectedSize ? this.displaySize(this.selectedSize) : '',
        model: this.selectedModel,
        parts: '',
      };

      Object.entries(this.pills).forEach(([key, pill]) => {
        if (!pill) return;
        const isActive = stage === key;
        const isSet = Boolean(values[key]);
        pill.classList.toggle('is-active', isActive);
        pill.classList.toggle('is-set', isSet);
        if (key === 'size') pill.disabled = !this.selectedBrand;
        if (key === 'model') pill.disabled = !this.selectedBrand || !this.selectedSize;
      });

      Object.entries(this.pillValues).forEach(([key, node]) => {
        if (!node) return;
        if (key === 'parts') {
          node.textContent = this.stage === 'parts' && this.total ? ` (${this.total})` : '';
        } else {
          node.textContent = values[key] ? `: ${values[key]}` : '';
        }
      });
    }

    /** Render the single visible stage, like the internal builder. While a
        search or category browse is active the funnel panel steps aside. */
    renderStage() {
      if (!this.stagePanel) return;

      if (this.searchTerm || this.selectedCategory) {
        this.stagePanel.hidden = true;
        return;
      }

      const stage = this.stage;
      if (stage === 'parts') {
        this.stagePanel.hidden = true;
        return;
      }

      this.stagePanel.hidden = false;
      if (stage === 'brand') this.renderBrandStage();
      else if (stage === 'size') this.renderSizeStage();
      else this.renderModelStage();
    }

    /** Brand tiles only render when the brand has a logo image (logoUrl from
        the parts-finder API). Brands without one used to show a 3-letter text
        placeholder; the client asked to drop those (2026-10-09). Adding a logo
        in the app makes the brand reappear automatically. Safety net: if no
        brand in this catalog has a logo, show them all rather than an empty
        step. */
    visibleBrands() {
      const brands = (this.facets && Array.isArray(this.facets.brands)) ? this.facets.brands : [];
      const withLogo = brands.filter((brand) => brand && typeof brand.logoUrl === 'string' && brand.logoUrl.trim() !== '');
      return withLogo.length ? withLogo : brands;
    }

    renderBrandStage() {
      const cards = this.visibleBrands().map(({ name, count, logoUrl }) => {
        const logo = logoUrl
          ? `<img class="repair-parts-finder__brand-logo" src="${this.escapeAttribute(logoUrl)}" alt="${this.escapeAttribute(name)}" loading="lazy">`
          : `<span class="repair-parts-finder__brand-fallback" aria-hidden="true">${this.escapeHTML(name.slice(0, 3))}</span>`;
        return `
          <button type="button" class="repair-parts-finder__brand-card" data-pick-brand="${this.escapeAttribute(name)}">
            ${logo}
            <span class="repair-parts-finder__brand-name">${this.escapeHTML(name)}</span>
          </button>
        `;
      }).join('');

      this.stagePanel.innerHTML = `
        <div class="repair-parts-finder__stage-card">
          <div class="repair-parts-finder__stage-heading">
            <h2>Choose your device's brand</h2>
            <p>It's printed on the body or nameplate of your backflow preventer.</p>
          </div>
          <div class="repair-parts-finder__brand-grid">${cards}</div>
        </div>
      `;
    }

    renderSizeStage() {
      const sizes = this.facets.sizesByBrand[this.selectedBrand] || [];
      const buttons = sizes.map(({ size, count }) => `
        <button type="button" class="repair-parts-finder__size-btn" data-pick-size="${this.escapeAttribute(size)}">
          ${this.escapeHTML(this.displaySize(size))}
        </button>
      `).join('');

      this.stagePanel.innerHTML = `
        <div class="repair-parts-finder__stage-card">
          <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
            <div>
              <h2>What size is your <em>${this.escapeHTML(this.selectedBrand)}</em>?</h2>
              <p>The pipe size is stamped on the device body, usually in inches.</p>
            </div>
            <button type="button" class="repair-parts-finder__back-link" data-stage-back="brand">Change brand</button>
          </div>
          <div class="repair-parts-finder__size-row">${buttons}</div>
        </div>
      `;
    }

    renderModelStage() {
      const models = (this.facets.modelsByBrandAndSize[this.selectedBrand] || {})[this.selectedSize] || [];
      const cards = models.map(({ model, count, imageUrl }) => {
        const image = imageUrl
          ? `<img class="repair-parts-finder__model-image" src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(`${this.selectedBrand} ${model}`)}" loading="lazy">`
          : '';
        return `
          <button type="button" class="repair-parts-finder__model-card${image ? '' : ' repair-parts-finder__model-card--text'}" data-pick-model="${this.escapeAttribute(model)}">
            ${image}
            <span class="repair-parts-finder__model-name">${this.escapeHTML(model)}</span>
          </button>
        `;
      }).join('');

      this.stagePanel.innerHTML = `
        <div class="repair-parts-finder__stage-card">
          <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
            <div>
              <h2>Which model? <em>${this.escapeHTML(this.selectedBrand)} &middot; ${this.escapeHTML(this.displaySize(this.selectedSize))}</em></h2>
              <p>The model number is on the nameplate. The same part can fit several models.</p>
            </div>
            <button type="button" class="repair-parts-finder__back-link" data-stage-back="size">Change size</button>
          </div>
          <div class="repair-parts-finder__model-grid">${cards}</div>
        </div>
      `;
    }

    renderCategories() {
      if (!this.categorySelect) return;
      // The split-out scopes (Complete Assemblies, Enclosures & Cages) are each
      // a single category, so the category dropdown only makes sense in the
      // Repair Parts scope — and since 2026-09-29 that scope is repair parts only,
      // so hide it too while there is just one category to pick.
      if (this.scope !== 'repair-parts' || this.facets.categories.length < 2) {
        this.categorySelect.hidden = true;
        return;
      }
      this.categorySelect.hidden = false;
      const placeholder = this.categorySelect.options[0];
      this.categorySelect.innerHTML = '';
      this.categorySelect.appendChild(placeholder);
      this.facets.categories.forEach(({ slug, label, count }) => {
        const option = document.createElement('option');
        option.value = slug;
        option.textContent = label;
        this.categorySelect.appendChild(option);
      });
    }

    /* ---- Products ---- */

    buildProductsUrl(page) {
      const params = new URLSearchParams();
      // Fixed param order keeps CDN cache keys canonical. Partial funnel
      // selections still narrow search/category results (admin parity).
      if (this.selectedBrand) params.set('brand', this.selectedBrand);
      if (this.selectedSize) params.set('size', this.selectedSize);
      if (this.selectedModel) params.set('model', this.selectedModel);
      if (this.selectedCategory) params.set('category', this.selectedCategory);
      if (this.searchTerm) params.set('q', this.searchTerm.toLowerCase());
      if (this.scope !== 'repair-parts') params.set('scope', this.scope);
      if (page > 1) params.set('page', String(page));
      return `${this.apiBase}/api/storefront/parts-finder/products?${params.toString()}`;
    }

    async fetchProducts(page) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.fetchSeq += 1;
      const seq = this.fetchSeq;

      this.resultsGrid.setAttribute('aria-busy', 'true');
      if (this.loadMoreButton) this.loadMoreButton.disabled = true;

      try {
        const response = await fetch(this.buildProductsUrl(page), {
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`Products request failed (${response.status})`);
        const payload = await response.json();
        if (seq !== this.fetchSeq) return;
        if (!payload.success) throw new Error('Products payload malformed');

        this.page = payload.page;
        this.total = payload.total;
        this.hasMore = payload.hasMore;
        this.renderCards(payload.products, page > 1);
      } catch (error) {
        if (error.name === 'AbortError' || seq !== this.fetchSeq) return;
        console.warn('Repair parts finder could not load products.', error);
        this.resultsGrid.innerHTML = '';
        this.total = 0;
        this.hasMore = false;
        if (this.emptyMessage) this.emptyMessage.hidden = false;
        if (this.loadMoreButton) this.loadMoreButton.hidden = true;
        this.renderResultsCopy();
        return;
      } finally {
        this.resultsGrid.removeAttribute('aria-busy');
        if (this.loadMoreButton) this.loadMoreButton.disabled = false;
      }

      if (this.emptyMessage) this.emptyMessage.hidden = this.total > 0;
      if (this.loadMoreButton) this.loadMoreButton.hidden = !this.hasMore;
      this.renderResultsCopy();
      this.renderPills();
    }

    renderResultsCopy() {
      if (this.resultsTitle) {
        const parts = `${this.total.toLocaleString()} ${this.itemWord(this.total)}`;
        if (this.hasCompleteSelection) {
          this.resultsTitle.textContent = `${parts} for ${this.selectedBrand} ${this.displaySize(this.selectedSize)} ${this.selectedModel}`;
        } else if (this.searchTerm) {
          this.resultsTitle.textContent = `${parts} matching "${this.searchTerm}"`;
        } else if (this.selectedCategory) {
          const category = this.facets.categories.find((c) => c.slug === this.selectedCategory);
          this.resultsTitle.textContent = `${parts} in ${category ? category.label : 'this category'}`;
        } else {
          this.resultsTitle.textContent = '';
        }
      }

      if (this.resultsMeta) {
        this.resultsMeta.hidden = !this.hasCompleteSelection;
        this.resultsMeta.textContent = 'The same SKU may work across multiple models.';
      }
    }

    renderCards(products, append) {
      const cards = products.map((product) => this.renderCard(product)).join('');
      if (append) {
        this.resultsGrid.insertAdjacentHTML('beforeend', cards);
      } else {
        this.resultsGrid.innerHTML = cards;
      }
    }

    renderCard(product) {
      const url = `/products/${encodeURIComponent(product.handle)}`;
      const image = product.imageUrl
        ? `<img src="${this.escapeAttribute(product.imageUrl)}" alt="${this.escapeAttribute(product.title)}" loading="lazy">`
        : '<div class="repair-parts-finder__card-placeholder" aria-hidden="true"></div>';
      const sku = product.variants.length === 1 && product.variants[0].sku
        ? `<p class="repair-parts-finder__card-sku">SKU: ${this.escapeHTML(product.variants[0].sku)}</p>`
        : '';
      // Devices 2.5"+ are quote-only: hide the price and show the quote CTA
      // instead (matches the PDP and collection cards).
      const priceHtml = this.isQuoteOnly(product)
        ? '<p class="repair-parts-finder__card-price repair-parts-finder__card-price--quote">Quote on request</p>'
        : `<p class="repair-parts-finder__card-price">${this.escapeHTML(
            product.priceMin === product.priceMax
              ? this.moneyFormatter.format(product.priceMin)
              : `From ${this.moneyFormatter.format(product.priceMin)}`
          )}</p>`;
      const chips = [
        ...(product.brand ? [product.brand] : []),
        ...product.sizes.slice(0, 3).map((size) => this.displaySize(size)),
        ...product.models.slice(0, 4),
      ];
      const chipsHtml = chips.length
        ? `<div class="repair-parts-finder__chips">${chips.map((chip) => `<span>${this.escapeHTML(chip)}</span>`).join('')}</div>`
        : '';

      return `
        <li class="repair-parts-finder__item">
          <div class="repair-parts-finder__card">
            <a class="repair-parts-finder__card-main" href="${this.escapeAttribute(url)}">
              <div class="repair-parts-finder__card-image">${image}</div>
              <div class="repair-parts-finder__card-content">
                ${sku}
                <h3 class="repair-parts-finder__card-title">${this.escapeHTML(product.title)}</h3>
                ${chipsHtml}
                ${priceHtml}
              </div>
            </a>
            <div class="repair-parts-finder__card-actions">
              ${this.renderCardAction(product, url)}
              <a class="repair-parts-finder__card-link" href="${this.escapeAttribute(url)}">View product</a>
            </div>
          </div>
        </li>
      `;
    }

    renderCardAction(product, url) {
      // Devices 2.5" and up can't be bought directly — we don't stock them
      // all, so a human confirms availability and price. Swap add-to-cart for
      // an "Add to quote" CTA (single variant) or send multi-variant products
      // to their page to choose options + quote there.
      if (this.isQuoteOnly(product)) {
        if (product.variants.length === 1) {
          const v = product.variants[0];
          return `
            <button type="button" class="btn btn--primary w-full js-add-to-quote"
              data-variant-id="${this.escapeAttribute(v.variantId)}"
              data-title="${this.escapeAttribute(product.title)}"
              data-sku="${this.escapeAttribute(v.sku || '')}"
              data-image="${this.escapeAttribute(product.imageUrl || '')}"
              data-handle="${this.escapeAttribute(product.handle)}">
              Add to quote
            </button>
          `;
        }
        return `<a class="btn btn--primary w-full" href="${this.escapeAttribute(url)}">Choose options to quote</a>`;
      }

      if (!product.available) {
        return '<button class="btn btn--primary w-full" type="button" disabled>Out of stock</button>';
      }

      if (product.variants.length === 1) {
        const cartAddUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add';
        // Minimal markup product-form.js needs: a .js-product-form form with a
        // variant id and an [name="add"] button. It AJAX-adds the item, updates
        // the cart icon, and opens the theme's cart drawer.
        return `
          <product-form>
            <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
            <form method="post" action="${this.escapeAttribute(cartAddUrl)}" class="js-product-form" novalidate>
              <input type="hidden" name="id" value="${this.escapeAttribute(product.variants[0].variantId)}">
              <button class="btn btn--primary w-full" name="add" aria-haspopup="dialog">Add to cart</button>
            </form>
          </product-form>
        `;
      }

      if (this.quickAddEnabled) {
        // Multi-variant: defer to the theme's quick-add drawer. Its delegated
        // document click handler picks up .js-quick-add buttons injected later.
        return `
          <button type="button"
                  class="btn btn--primary w-full js-quick-add"
                  aria-haspopup="dialog"
                  data-product-url="${this.escapeAttribute(url)}"
                  data-product-default-variant="false">
            Choose options
          </button>
        `;
      }

      return `<a class="btn btn--primary w-full" href="${this.escapeAttribute(url)}">Choose options</a>`;
    }

    /* ---- Utils ---- */

    /** Catalog-aware noun so counts read naturally per tab: "repair parts" in
        Repair Parts, "assemblies" in Complete Assemblies, "enclosures" in
        Enclosures & Cages. */
    itemWord(count) {
      if (this.scope === 'complete-assemblies') return count === 1 ? 'assembly' : 'assemblies';
      if (this.scope === 'enclosures-and-cages') return count === 1 ? 'enclosure' : 'enclosures';
      if (this.scope === 'shutoff-valves') return count === 1 ? 'valve' : 'valves';
      return count === 1 ? 'repair part' : 'repair parts';
    }

    /** Devices 2.5" and up route to "request a quote" instead of add-to-cart.
        A product is quote-only if the funnel size or any of its tagged sizes
        is >= 2.5" — i.e. it pertains to a larger assembly we may not stock. */
    isQuoteOnly(product) {
      // The app is the source of truth: it resolves purchasable-now /
      // add-to-quote tags and the size+cage fallback. Only fall back to the
      // local size check when the API did not say.
      if (product && product.quoteOnly === true) return true;
      if (product && product.quoteOnly === false) return false;
      const THRESHOLD = 2.5;
      if (this.selectedSize && this.sizeValue(this.selectedSize) >= THRESHOLD) return true;
      const sizes = Array.isArray(product.sizes) ? product.sizes : [];
      return sizes.some((s) => this.sizeValue(s) >= THRESHOLD);
    }

    /** Parse a canonical size ("2 1/2", "1/2", "3", "2.5", "2-1/2") into a
        number of inches, mirroring the app's sizeToNumber. This is a UX gate
        only — the server independently enforces the same 2.5" threshold on
        the quote endpoint. Unknown/blank => 0 (treated as not gated). */
    sizeValue(size) {
      if (!size) return 0;
      const clean = String(size).replace(/"/g, '').replace(/-/g, ' ').trim();
      const parts = clean.split(/\s+/);
      if (parts.length >= 2 && parts[1].includes('/')) {
        const [n, d] = parts[1].split('/').map(Number);
        return (Number(parts[0]) || 0) + (d ? n / d : 0);
      }
      if (parts[0].includes('/')) {
        const [n, d] = parts[0].split('/').map(Number);
        return d ? n / d : 0;
      }
      return Number(parts[0]) || 0;
    }

    /** Sizes as fractions (1 ½", not 1.5") — display only; filtering keeps the
        API's value. Mirrors the app's src/lib/size-display.ts. Null = not a size. */
    fractionSize(size) {
      const s = String(size).replace(/["”″]/g, '').trim();
      let v = null;
      const mixed = s.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/);
      const frac = s.match(/^(\d+)\/(\d+)$/);
      if (mixed && Number(mixed[3])) v = Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
      else if (frac && Number(frac[2])) v = Number(frac[1]) / Number(frac[2]);
      else if (/^(\d+\.?\d*|\.\d+)$/.test(s)) v = Number(s);
      if (v === null || !Number.isFinite(v)) return null;
      const n = Math.round(v * 16);
      if (Math.abs(v * 16 - n) > 0.05) return s;
      const whole = Math.floor(n / 16);
      const rest = n % 16;
      if (!rest) return String(whole);
      const gcd = (a, b) => (b ? gcd(b, a % b) : a);
      const d = gcd(rest, 16);
      const key = `${rest / d}/${16 / d}`;
      const glyph = { '1/2': '½', '1/4': '¼', '3/4': '¾', '1/8': '⅛', '3/8': '⅜', '5/8': '⅝', '7/8': '⅞' }[key] || key;
      return whole ? `${whole} ${glyph}` : glyph;
    }

    /** API sizes are canonical fractions ("1 1/2"); shown as 1 ½" with an
        inch mark — display only, matching stays canonical. */
    displaySize(size) {
      const f = this.fractionSize(size);
      return f === null ? size : `${f}"`;
    }

    escapeHTML(value) {
      return String(value).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      }[char]));
    }

    escapeAttribute(value) {
      return this.escapeHTML(value);
    }
  }

  customElements.define('repair-parts-finder', RepairPartsFinder);
}
